import { useId } from "react";
import { TextInput } from "@/components/ui/Field";
import { rabattFromListenpreisEk } from "@/lib/artikelRabatt";
import {
  formatMoneyDe,
  staffelLabel,
  vkFromEk,
  type ArtikelZuschlagState,
  type ZuschlagModus,
  type ZuschlagStaffelRow,
} from "@/lib/artikelZuschlag";
import {
  ArtikelPriceAktivBadge,
  ArtikelPricePassendBadge,
} from "@/pages/artikel/artikelPriceBadges";

export type ZuschlagSelection =
  | { modus: "inaktiv" }
  | { modus: "regel"; regel_id: number }
  | { modus: "manuell_prozent"; manuell_prozent: number | null; manuell_prozent_raw: string }
  | { modus: "manuell_vk"; verkaufspreis: number | null; verkaufspreis_raw: string };

function parseNum(raw: string): number | null {
  const s = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function initialSelection(state: ArtikelZuschlagState | null | undefined): ZuschlagSelection {
  const staffeln = state?.staffeln ?? [];
  const modus = state?.modus;

  if (modus === "manuell_prozent") {
    const pct = state?.manuell_prozent ?? state?.aufschlag_prozent ?? null;
    return {
      modus: "manuell_prozent",
      manuell_prozent: pct,
      manuell_prozent_raw: pct != null ? String(pct) : "",
    };
  }
  if (modus === "manuell_vk") {
    const vk = state?.vk ?? null;
    return {
      modus: "manuell_vk",
      verkaufspreis: vk,
      verkaufspreis_raw: vk != null ? String(vk) : "",
    };
  }
  const selectedId = modus === "regel" ? state?.regel_id : null;
  if (selectedId != null && staffeln.some((s) => s.id === selectedId)) {
    return { modus: "regel", regel_id: selectedId };
  }
  return { modus: "inaktiv" };
}

export function selectionFromZuschlagState(
  state: ArtikelZuschlagState | null | undefined,
): ZuschlagSelection {
  return initialSelection(state);
}

export function previewVk(
  selection: ZuschlagSelection,
  ek: number | null | undefined,
  staffeln: ZuschlagStaffelRow[],
): number | null {
  if (selection.modus === "inaktiv") return null;
  if (selection.modus === "regel") {
    const row = staffeln.find((s) => s.id === selection.regel_id);
    if (!row) return null;
    if (row.vk != null) return row.vk;
    if (ek != null && ek > 0) return vkFromEk(ek, row.zuschlag_prozent);
    return null;
  }
  if (selection.modus === "manuell_prozent") {
    if (ek == null || ek <= 0 || selection.manuell_prozent == null) return null;
    return vkFromEk(ek, selection.manuell_prozent);
  }
  return selection.verkaufspreis;
}

export function ArtikelZuschlagSection({
  state,
  selection,
  onChange,
  disabled,
  ekOverride,
  listenpreis,
  rabatt,
}: {
  state: ArtikelZuschlagState | null | undefined;
  selection: ZuschlagSelection;
  onChange: (next: ZuschlagSelection) => void;
  disabled?: boolean;
  /** Formular-EK (netto), falls abweichend vom geladenen Zustand. */
  ekOverride?: number | null;
  /** Hero-/Listenpreis netto — für Anzeige des Rabatts auf den Listenpreis. */
  listenpreis?: number | null;
  /** Gespeicherter Rabatt %; sonst aus Listenpreis und EK berechnet. */
  rabatt?: number | null;
}) {
  const radioName = useId();
  const staffeln = state?.staffeln ?? [];
  const ek = ekOverride !== undefined ? ekOverride : (state?.ek_netto ?? null);
  const ekOk = ek != null && ek > 0;
  const listenpreisOk =
    listenpreis != null && Number.isFinite(Number(listenpreis)) && Number(listenpreis) > 0;
  const rabattPct =
    rabatt != null && Number.isFinite(Number(rabatt))
      ? Number(rabatt)
      : rabattFromListenpreisEk(listenpreis, ek);
  const matchedId = (() => {
    if (!ekOk) return null;
    let best: ZuschlagStaffelRow | null = null;
    for (const row of staffeln) {
      if (ek! + 1e-9 >= (Number(row.ab_ek_netto) || 0)) {
        if (!best || (Number(row.ab_ek_netto) || 0) >= (Number(best.ab_ek_netto) || 0)) best = row;
      }
    }
    return best?.id ?? null;
  })();
  const displayStaffeln = staffeln.map((row) => ({
    ...row,
    vk: ekOk ? vkFromEk(ek!, row.zuschlag_prozent) : null,
    is_match: matchedId != null && row.id === matchedId,
  }));
  const preview =
    selection.modus === "inaktiv"
      ? (state?.vk ?? null)
      : previewVk(selection, ek, displayStaffeln);

  function selectRegel(id: number) {
    onChange({ modus: "regel", regel_id: id });
  }

  function selectManuellProzent() {
    const raw =
      selection.modus === "manuell_prozent"
        ? selection.manuell_prozent_raw
        : state?.manuell_prozent != null
          ? String(state.manuell_prozent)
          : state?.aufschlag_prozent != null
            ? String(state.aufschlag_prozent)
            : "";
    onChange({
      modus: "manuell_prozent",
      manuell_prozent: parseNum(raw),
      manuell_prozent_raw: raw,
    });
  }

  function selectManuellVk() {
    const raw =
      selection.modus === "manuell_vk"
        ? selection.verkaufspreis_raw
        : state?.vk != null
          ? String(state.vk)
          : preview != null
            ? String(preview)
            : "";
    onChange({
      modus: "manuell_vk",
      verkaufspreis: parseNum(raw),
      verkaufspreis_raw: raw,
    });
  }

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-label">Verkaufspreis / Zuschläge</h3>
        <p className="mt-1 text-xs text-label-secondary">
          EK netto: {ekOk ? `${formatMoneyDe(ek)} €` : "—"}.
          {listenpreisOk ? <> Listenpreis: {formatMoneyDe(Number(listenpreis))} €.</> : null}{" "}
          Entweder ein Rabatt oder ein Zuschlag ist aktiv, nicht beides.
        </p>
      </div>

      {displayStaffeln.length > 0 ? (
        <ul className="space-y-1 rounded-ios border border-separator divide-y divide-separator">
          {displayStaffeln.map((row) => {
            const checked = selection.modus === "regel" && selection.regel_id === row.id;
            const vkLabel = row.vk != null ? `${formatMoneyDe(row.vk)} €` : "—";
            return (
              <li key={row.id}>
                <label
                  className={
                    checked
                      ? "flex cursor-pointer items-center gap-3 bg-accent/5 px-3 py-2.5"
                      : "flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-card-elevated/60"
                  }
                >
                  <input
                    type="radio"
                    name={radioName}
                    className="shrink-0"
                    checked={checked}
                    disabled={disabled || !ekOk}
                    onChange={() => selectRegel(row.id)}
                  />
                  <span className="min-w-0 flex-1 text-sm text-label">
                    {staffelLabel(row)}
                    {checked ? <ArtikelPriceAktivBadge /> : null}
                    {!checked && row.is_match ? <ArtikelPricePassendBadge /> : null}
                  </span>
                  <span className="shrink-0 font-mono text-sm tabular-nums text-label">
                    VK {vkLabel}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-label-tertiary">
          Keine Zuschlagsstaffeln konfiguriert. Verkaufspreis manuell setzen.
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <label
          className={
            selection.modus === "manuell_prozent"
              ? "flex cursor-pointer items-start gap-3 rounded-ios border border-accent bg-accent/5 px-3 py-2.5"
              : "flex cursor-pointer items-start gap-3 rounded-ios border border-separator px-3 py-2.5"
          }
        >
          <input
            type="radio"
            name={radioName}
            className="mt-2 shrink-0"
            checked={selection.modus === "manuell_prozent"}
            disabled={disabled || !ekOk}
            onChange={selectManuellProzent}
          />
          <div className="min-w-0 flex-1">
            <TextInput
              label={
                <>
                  Eigener Zuschlag %
                  {selection.modus === "manuell_prozent" ? <ArtikelPriceAktivBadge /> : null}
                </>
              }
              value={selection.modus === "manuell_prozent" ? selection.manuell_prozent_raw : ""}
              disabled={disabled || selection.modus !== "manuell_prozent"}
              inputMode="decimal"
              onFocus={() => {
                if (selection.modus !== "manuell_prozent") selectManuellProzent();
              }}
              onChange={(e) => {
                const raw = e.target.value;
                onChange({
                  modus: "manuell_prozent",
                  manuell_prozent: parseNum(raw),
                  manuell_prozent_raw: raw,
                });
              }}
            />
          </div>
        </label>

        <label
          className={
            selection.modus === "manuell_vk"
              ? "flex cursor-pointer items-start gap-3 rounded-ios border border-accent bg-accent/5 px-3 py-2.5"
              : "flex cursor-pointer items-start gap-3 rounded-ios border border-separator px-3 py-2.5"
          }
        >
          <input
            type="radio"
            name={radioName}
            className="mt-2 shrink-0"
            checked={selection.modus === "manuell_vk"}
            disabled={disabled}
            onChange={selectManuellVk}
          />
          <div className="min-w-0 flex-1">
            <TextInput
              label={
                <>
                  VK manuell
                  {selection.modus === "manuell_vk" ? <ArtikelPriceAktivBadge /> : null}
                </>
              }
              value={selection.modus === "manuell_vk" ? selection.verkaufspreis_raw : ""}
              disabled={disabled || selection.modus !== "manuell_vk"}
              inputMode="decimal"
              onFocus={() => {
                if (selection.modus !== "manuell_vk") selectManuellVk();
              }}
              onChange={(e) => {
                const raw = e.target.value;
                onChange({
                  modus: "manuell_vk",
                  verkaufspreis: parseNum(raw),
                  verkaufspreis_raw: raw,
                });
              }}
            />
          </div>
        </label>
      </div>

      <p className="text-sm text-label-secondary">
        Wirksamer VK:{" "}
        <span className="font-mono tabular-nums text-label">
          {preview != null ? `${formatMoneyDe(preview)} €` : "—"}
        </span>
        {rabattPct != null ? (
          <>
            {" · "}
            Rabatt auf Listenpreis:{" "}
            <span className="font-mono tabular-nums text-label">
              {formatMoneyDe(rabattPct)} %
            </span>
            {listenpreisOk ? (
              <span className="text-label-tertiary">
                {" "}
                (LP {formatMoneyDe(Number(listenpreis))} €)
              </span>
            ) : null}
          </>
        ) : null}
      </p>
    </section>
  );
}

export function selectionToSaveBody(selection: ZuschlagSelection): {
  modus: ZuschlagModus;
  regel_id?: number | null;
  manuell_prozent?: number | null;
  verkaufspreis?: number | null;
} | { error: string } {
  if (selection.modus === "inaktiv") {
    return { error: "Kein Zuschlag ausgewählt." };
  }
  if (selection.modus === "regel") {
    return { modus: "regel", regel_id: selection.regel_id };
  }
  if (selection.modus === "manuell_prozent") {
    if (selection.manuell_prozent == null || selection.manuell_prozent < 0) {
      return { error: "Bitte einen gültigen eigenen Zuschlag % eingeben." };
    }
    return { modus: "manuell_prozent", manuell_prozent: selection.manuell_prozent };
  }
  if (selection.verkaufspreis == null || selection.verkaufspreis < 0) {
    return { error: "Bitte einen gültigen manuellen Verkaufspreis eingeben." };
  }
  return { modus: "manuell_vk", verkaufspreis: selection.verkaufspreis };
}
